import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, HardHat, Landmark, Building2, Shield, FileCheck, DollarSign, FileText, BarChart3, Briefcase, TrendingUp } from 'lucide-react';
import { BostonFederalName } from '../components/RegisteredName';

const categories = [
  {
    label: 'Construction',
    title: 'Construction & Contractors',
    subtitle: 'Built for the Field',
    desc: 'Deep experience across the full spectrum of construction — from specialty contractors to general contractors to large-scale commercial builders with complex compliance needs.',
    industries: [
      { icon: HardHat, name: 'Union Contractors', detail: 'Multi-trade union payroll, benefit fund management, and certified reporting.' },
      { icon: Shield, name: 'Commercial Contractors', detail: 'Full financial infrastructure for commercial construction contractors.' },
      { icon: FileCheck, name: 'Certified Payroll Projects', detail: 'Specialized certified payroll for Davis-Bacon and prevailing wage projects.' },
      { icon: DollarSign, name: 'Prevailing Wage Projects', detail: 'End-to-end prevailing wage determination, monitoring, and compliance.' },
    ],
  },
  {
    label: 'Government & Public',
    title: 'Government & Public Sector',
    subtitle: 'Public Project Specialists',
    desc: 'Specialized expertise in the compliance requirements of federally and state-funded work — DCAMM, public construction, and government contract environments.',
    industries: [
      { icon: Landmark, name: 'Federal Projects', detail: 'Compliance-intensive payroll for federally-funded construction and services.' },
      { icon: Building2, name: 'DCAMM & Public Construction', detail: 'Massachusetts DCAMM projects and public construction sector management.' },
      { icon: FileText, name: 'AIA Billing & Requisitions', detail: 'G702/G703 AIA billing, payment applications, and contract billing management.' },
      { icon: BarChart3, name: 'WIP & Job Cost Reporting', detail: 'Work-in-progress schedules and job-cost management for bonding readiness.' },
    ],
  },
  {
    label: 'Commercial & Corporate',
    title: 'Commercial & Corporate',
    subtitle: 'Operational Excellence',
    desc: 'Financial infrastructure, controls, and reporting for commercial businesses that need the discipline and visibility to grow, scale, and access capital.',
    industries: [
      { icon: Briefcase, name: 'Bank & Bonding Readiness', detail: 'Financial packages, surety bonding support, and lender-ready reporting.' },
      { icon: TrendingUp, name: 'Growth Companies', detail: 'Infrastructure and reporting for high-growth businesses.' },
      { icon: Shield, name: 'Multi-Entity Businesses', detail: 'Consolidated reporting and controls across complex business structures.' },
      { icon: BarChart3, name: 'Compliance-Driven Operations', detail: 'Operations with complex reporting, regulatory, or audit requirements.' },
    ],
  },
  {
    label: 'Portfolio & Capital',
    title: 'Portfolio & Capital-Backed',
    subtitle: 'Investor-Ready Operations',
    desc: 'Financial infrastructure and reporting for growth-stage companies backed by funds, accelerators, or CDFIs — helping them achieve the capital readiness investors expect.',
    industries: [
      { icon: TrendingUp, name: 'PE & VC Portfolio Companies', detail: 'Financial infrastructure for PE and VC-backed growth companies.' },
      { icon: Landmark, name: 'CDFI-Backed Businesses', detail: 'Community development lending portfolio financial management.' },
      { icon: Building2, name: 'Accelerator Graduates', detail: 'Post-accelerator financial infrastructure and controls.' },
      { icon: Briefcase, name: 'Pre-Investment Readiness', detail: 'Preparing companies to be fundable and capital-ready.' },
    ],
  },
];

export default function Industries() {
  const [activeTab, setActiveTab] = useState(0);
  const cat = categories[activeTab];

  return (
    <div>
      {/* ── Hero ─────────────────────────────────── */}
      <section className="grid-bg pt-32 pb-14" data-testid="industries-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <p className="text-[#1FA8F5] text-[10px] font-bold uppercase tracking-[0.25em] mb-4">Industries We Serve</p>
            <h1 className="text-5xl sm:text-6xl font-bold text-[#0B1F33] leading-[1.05] tracking-tight mb-5 max-w-3xl">
              Built for Complex,<br /><span className="text-[#1FA8F5]">Compliance-Heavy Industries</span>
            </h1>
            <p className="text-lg text-[#40516A] max-w-2xl leading-relaxed">
              <BostonFederalName />'s experience runs deep in industries where compliance, reporting, and financial precision are non-negotiable. We understand your challenges — and we're equipped to solve them.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ── Category Tab Section ─────────────────── */}
      <section className="bg-white pb-20" data-testid="industries-tabs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Tab Bar */}
          <div>
            <div className="flex overflow-x-auto no-scrollbar">
              {categories.map((c, i) => (
                <button
                  key={c.label}
                  onClick={() => setActiveTab(i)}
                  data-testid={`industry-tab-${i}`}
                  className={`px-6 py-4 text-sm font-semibold whitespace-nowrap transition-colors duration-200 ${
                    i === activeTab ? 'text-[#1FA8F5]' : 'text-[#40516A] hover:text-[#0B1F33]'
                  }`}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.28 }}
              className="py-14"
            >
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-12">
                <div className="lg:col-span-5">
                  <p className="text-[#1FA8F5] text-[10px] font-bold uppercase tracking-[0.2em] mb-2">{cat.subtitle}</p>
                  <h2 className="text-3xl font-bold text-[#0B1F33] mb-4 leading-tight">{cat.title}</h2>
                  <p className="text-[#40516A] leading-relaxed mb-6">{cat.desc}</p>
                  <Link to="/contact" data-testid="industry-cta" className="inline-flex items-center gap-1.5 text-[#1FA8F5] font-bold text-xs uppercase tracking-wider hover:gap-3 transition-all">
                    Start a Conversation <ArrowRight size={13} />
                  </Link>
                </div>
                <div className="lg:col-span-7">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-8">
                    {cat.industries.map((ind, i) => {
                      const Icon = ind.icon;
                      return (
                        <motion.div
                          key={ind.name}
                          initial={{ opacity: 0, y: 12 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: i * 0.07 }}
                          data-testid={`industry-item-${i}`}
                        >
                          <div className="flex items-start gap-4">
                            <div className="w-9 h-9 flex items-center justify-center flex-shrink-0 mt-0.5">
                              <Icon size={17} strokeWidth={1.5} className="text-[#1FA8F5]" />
                            </div>
                            <div>
                              <h3 className="text-[#0B1F33] font-semibold text-[15px] mb-1">{ind.name}</h3>
                              <p className="text-[#40516A] text-sm leading-relaxed">{ind.detail}</p>
                            </div>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      {/* ── Dark CTA ──────────────────────────────── */}
      <section className="dark-grid-bg py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}>
            <h2 className="text-4xl font-bold text-white mb-4">Your Industry. Our Expertise.</h2>
            <p className="text-slate-400 text-lg mb-8 max-w-xl mx-auto">Tell us about your business and we'll show you how <BostonFederalName /> can help.</p>
            <Link to="/contact" data-testid="industries-cta" className="bg-[#1FA8F5] text-white px-8 py-3.5 rounded-md font-semibold hover:bg-[#0B8ED8] transition-colors inline-flex items-center gap-2">
              Start a Conversation <ArrowRight size={17} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
